import React from 'react';

import { useSelector } from 'react-redux';
import { getDashboardsState, getIsSelfHosted } from '@/store-selectors';
import flex from '@/css/module/flex.css';
import { Small, TrevorIcon } from '@trevor/ui-components';
import { Permission } from '@/permissions/Permission';
import styled from 'styled-components';
import { useDashboardId, useDatasourceId } from '@/hooks';
import { openReactModal } from '@/actions';
import { useAppDispatch } from '@/store/hooks';
import { userAction } from '@/util/helpers/ajax-helpers';
import style from '@/css/module/dashboard-filters.css';
import DashboardFilters from '@/components/DashboardFilters_LEGACY';
import ShareIcon from '@/components/ShareIcon';
import { EMAIL, SLACK } from '@/util/AlertRef';
import SendDashboard from '@/components/SendDashboard';
import { ShareDashboardButton } from '@/features/dashboard/components/DashboardFilterBar/components/ShareDashboardButton';
import { usePermission } from '@/hooks/usePermission';

const DeliveryOptionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-s);
  margin-right: 20px;
`;

const ScheduleViaDeliveryLabel = styled(Small)`
  color: var(--color-interface-content-normal);
`;

export const PrivateDashboardFilterBar: React.FC<{
  updateDashboardFilters: F0;
}> = ({ updateDashboardFilters }) => {
  const dashboardState = useSelector(getDashboardsState);
  const datasourceId = useDatasourceId();
  const dashboardId = useDashboardId();
  const isSelfHosted = useSelector(getIsSelfHosted);
  const dashboard = dashboardState.active;
  const dispatch = useAppDispatch();
  const readonly = !usePermission(
    `${datasourceId}.dashboards.${dashboardId}.write`,
  );

  const snapshotButtonsDisabled = () => {
    const { filters, appliedFilters } = dashboardState;
    for (const value of appliedFilters) {
      const fr = filters.findIndex((f) => value.lhs().name() === f.name());
      if (fr >= 0) {
        return true;
      }
    }
    return false;
  };

  const onSetupDashboardPush =
    // @ts-expect-error: infer types.
    (dashboardToPush, defaultDestination) => () => {
      userAction('open-dashboard-delivery-modal');
      dispatch(
        openReactModal(
          <SendDashboard
            defaultDestination={defaultDestination}
            dashboard={dashboardToPush}
          />,
        ),
      );
    };

  const clearFiltersTooltip =
    'Please clear your dashboard filters before sharing with team';

  const snapshotEmailTooltip = () => {
    return !snapshotButtonsDisabled()
      ? 'Deliver dashboard snapshot to Email'
      : null;
  };

  const slackEmailTooltip = () => {
    return !snapshotButtonsDisabled()
      ? 'Deliver dashboard snapshot to Slack'
      : null;
  };

  // TODO: Check if this required
  // const shareButtonTooltip = () => {
  //   return !snapshotButtonsDisabled() ? 'Share dashboard with team' : null;
  // };
  //
  // const shareButtonDisabledTooltip = () => {
  //   return !snapshotButtonsDisabled()
  //     ? '(query still running)'
  //     : clearFiltersTooltip;
  // };

  return (
    <div
      className={`w-100 ${flex.justifyContentBetween} ${flex.alignItemsCenter} ${style.stickyTopBar}`}
    >
      <Permission
        permissionPath={`${datasourceId}.dashboards.${dashboardId}.read`}
      >
        <div className={flex.grow}>
          {!dashboardState.chartWidgets ||
          dashboardState.chartWidgets.length === 0 ||
          dashboardState.public ? null : (
            <DashboardFilters
              disabled={dashboardState.unsavedChanges}
              onUpdated={updateDashboardFilters}
              readonly={readonly}
            />
          )}
        </div>
      </Permission>

      <DeliveryOptionsContainer>
        {isSelfHosted ? null : (
          <Permission
            permissionPath={`${datasourceId}.dashboards.${dashboardId}.write`}
          >
            <>
              <ScheduleViaDeliveryLabel>
                Schedule delivery via:
              </ScheduleViaDeliveryLabel>
              <ShareIcon
                id="email"
                tooltip={snapshotEmailTooltip()}
                onClick={onSetupDashboardPush(dashboard, EMAIL)}
                disabled={snapshotButtonsDisabled()}
                disabledTip={clearFiltersTooltip}
              >
                <TrevorIcon name="mail" />
              </ShareIcon>
              <ShareIcon
                id="slack"
                tooltip={slackEmailTooltip()}
                onClick={onSetupDashboardPush(dashboard, SLACK)}
                disabled={snapshotButtonsDisabled()}
                disabledTip={clearFiltersTooltip}
              >
                <TrevorIcon name="slack" />
              </ShareIcon>
              <div
                style={{
                  height: '25px',
                  borderRight: '1px solid #ddd',
                }}
              />
            </>
          </Permission>
        )}
        <ShareDashboardButton />
      </DeliveryOptionsContainer>
    </div>
  );
};
